import React from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Search,
  Package,
  Wrench,
  Trash2,
  Plus,
  GripVertical,
  FilePlus,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppSelect } from '../../../components/AppSelect';
import { GermanDecimalInput } from '../../../components/GermanDecimalInput';
import { offerUnitSelectOptions, OFFER_UNIT_OPTIONS } from '../../../constants/offerUnits';
import type { LeadPosition, OfferDiscount, OfferVatMode, PositionRowId } from '../../../types';
import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import { DEFAULT_OFFER_VAT_MODE, getOfferVatOption, isZeroVatSpecialMode } from '../utils/offerVat';
import { vkFromEkSurcharge } from '../utils/offerPricing';
import { OfferImportActionButton } from './OfferImportActionButton';
import { GaebActionButton } from './GaebActionButton';
import { AppModal, AppModalHeader } from '../../../components/AppModal';
import { IdsCartButton } from './IdsCartButton';
import { offeringPositionsApiBase } from '../../../utils/offeringApi';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useModuleAccess } from '../../../hooks/useModuleAccess';

type StammdatenMaterial = {
  id: string;
  name: string;
  unit: string | null;
  gross_price: number | null;
  net_price: number | null;
  surcharge_percent: number | null;
  description: string | null;
};

type StammdatenLeistung = {
  id: string;
  name: string;
  unit: string | null;
  gross_price: number | null;
  net_cost: number | null;
  surcharge_percent: number | null;
  description: string | null;
};

const eurFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const decFmt = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctFmt = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function toInputDecimal(n: number): string {
  if (!Number.isFinite(n)) return '0,00';
  return decFmt.format(n);
}

function toInputPercent(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return pctFmt.format(n);
}

function normalizePositionsList(data: unknown): LeadPosition[] {
  if (!Array.isArray(data)) return [];
  return data.map((row) => {
    const r = row as LeadPosition;
    return { ...r, item_name: r.item_name ?? '', internal_note: r.internal_note ?? '' };
  });
}

type LeadPositionPatch = Partial<
  Pick<
    LeadPosition,
    'quantity' | 'unit' | 'item_name' | 'description' | 'internal_note' | 'unit_cost_ek' | 'surcharge_percent' | 'unit_price_vk'
  >
>;

export type LeadProdukteTabOfferingMeta = {
  title: string;
  displayId?: string | null;
  isLastUsed?: boolean;
  isPrimary?: boolean;
};

/** Max. Angebots-Blöcke pro Seite im Tab „Angebot“. */
export const OFFERINGS_PAGE_SIZE = 10;

export type LeadProdukteTabOfferingSection = {
  offeringId: string;
  offeringMeta: LeadProdukteTabOfferingMeta;
  offerDiscount?: OfferDiscount | null;
  offerVatMode?: OfferVatMode;
};

type LeadProdukteTabProps = {
  leadRouteId: string;
  /** Alle Angebote des Projekts (gestapelt). Leer = Legacy-Positionen auf Lead-Ebene. */
  offeringSections?: LeadProdukteTabOfferingSection[];
  /** Nettosumme aller Angebote für die Lead-Kopfzeile (ohne MwSt). */
  onNetSumChange?: (netSum: number) => void;
  onCreateDocument?: () => void;
};

type LeadProdukteOfferingSectionProps = {
  leadRouteId: string;
  offeringId: string | null;
  offeringMeta?: LeadProdukteTabOfferingMeta | null;
  offerDiscount?: OfferDiscount | null;
  offerVatMode?: OfferVatMode;
  onNetSumChange?: (netSum: number) => void;
  /** Erste Sektion ohne oberen Rand. */
  isFirst?: boolean;
};

const LeadProdukteOfferingSection: React.FC<LeadProdukteOfferingSectionProps> = ({
  leadRouteId,
  offeringId,
  offeringMeta,
  offerDiscount,
  offerVatMode = DEFAULT_OFFER_VAT_MODE,
  onNetSumChange,
  isFirst = false,
}) => {
  const [positions, setPositions] = React.useState<LeadPosition[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [savingId, setSavingId] = React.useState<PositionRowId | null>(null);
  const [reordering, setReordering] = React.useState(false);
  const [picker, setPicker] = React.useState<'material' | 'leistung' | null>(null);
  const [materials, setMaterials] = React.useState<StammdatenMaterial[]>([]);
  const [leistungen, setLeistungen] = React.useState<StammdatenLeistung[]>([]);
  const [pickerSearch, setPickerSearch] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [mobileEditId, setMobileEditId] = React.useState<PositionRowId | null>(null);
  const [mobileEditDraft, setMobileEditDraft] = React.useState<Partial<LeadPosition>>({});
  // Material/Leistung kommen aus den Stammdaten, der Warenkorb aus der IDS-Integration.
  const { can } = useCapabilities();
  const canViewStammdaten = can('web.stammdaten.view');
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const idsEnabled = modulesLoaded && isEnabled('ids');

  const baseUrl = React.useMemo(
    () =>
      offeringId
        ? offeringPositionsApiBase(offeringId)
        : `/api/leads/${encodeURIComponent(leadRouteId)}/positions`,
    [offeringId, leadRouteId],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadPositions = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(baseUrl);
      if (!res.ok) throw new Error('Positionen konnten nicht geladen werden.');
      const data = await res.json();
      setPositions(normalizePositionsList(data));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  React.useEffect(() => {
    void loadPositions();
  }, [loadPositions]);

  const loadStammdaten = React.useCallback(async (kind: 'material' | 'leistung') => {
    try {
      if (kind === 'material') {
        const res = await fetch('/api/materials');
        const list = (res.ok ? await res.json() : []) as unknown;
        setMaterials(Array.isArray(list) ? (list as StammdatenMaterial[]) : []);
      } else {
        const res = await fetch('/api/leistungen');
        const list = (res.ok ? await res.json() : []) as unknown;
        setLeistungen(Array.isArray(list) ? (list as StammdatenLeistung[]) : []);
      }
    } catch {
      if (kind === 'material') setMaterials([]);
      else setLeistungen([]);
    }
  }, []);

  React.useEffect(() => {
    if (!picker || !canViewStammdaten) return;
    void loadStammdaten(picker);
    setPickerSearch('');
  }, [picker, loadStammdaten, canViewStammdaten]);

  const rawNetSum = React.useMemo(
    () =>
      positions.reduce(
        (sum, p) => sum + (Number.isFinite(p.line_total) ? p.line_total : Math.round(p.quantity * p.unit_price_vk * 100) / 100),
        0
      ),
    [positions]
  );
  const discountAmount = React.useMemo(() => {
    if (!offerDiscount || offerDiscount.value <= 0) return 0;
    return offerDiscount.type === 'percent'
      ? Math.round(rawNetSum * (offerDiscount.value / 100) * 100) / 100
      : Math.min(offerDiscount.value, rawNetSum);
  }, [rawNetSum, offerDiscount]);
  const netSum = rawNetSum - discountAmount;
  const vatOption = React.useMemo(() => getOfferVatOption(offerVatMode), [offerVatMode]);
  const vatRatePercent = vatOption.ratePercent;
  const vatAmount = React.useMemo(() => Math.round(netSum * (vatRatePercent / 100) * 100) / 100, [netSum, vatRatePercent]);
  const grossSum = React.useMemo(() => Math.round((netSum + vatAmount) * 100) / 100, [netSum, vatAmount]);
  const hideVatAndGross = React.useMemo(() => isZeroVatSpecialMode(offerVatMode), [offerVatMode]);
  const vatLegalNote = vatOption.legalNote ?? '';

  React.useEffect(() => {
    onNetSumChange?.(netSum);
  }, [netSum, onNetSumChange]);

  const addFreeRow = async () => {
    setAdding(true);
    setError(null);
    try {
      const res = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_type: 'custom',
          quantity: 1,
          unit: 'Stk',
          description: '',
          unit_cost_ek: 0,
          surcharge_percent: 0,
          unit_price_vk: 0,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'Speichern fehlgeschlagen');
      }
      await loadPositions();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setAdding(false);
    }
  };

  const addFromStamm = async (kind: 'material' | 'leistung', id: string) => {
    setAdding(true);
    setError(null);
    try {
      const body = kind === 'material' ? { material_id: id } : { leistung_id: id };
      const res = await fetch(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'Hinzufügen fehlgeschlagen');
      }
      setPicker(null);
      await loadPositions();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setAdding(false);
    }
  };

  const saveRow = async (p: LeadPosition, patch: LeadPositionPatch) => {
    const body: Record<string, unknown> = {};
    if (patch.quantity !== undefined) body.quantity = patch.quantity;
    if (patch.unit !== undefined) body.unit = patch.unit;
    if (patch.item_name !== undefined) body.item_name = patch.item_name;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.internal_note !== undefined) body.internal_note = patch.internal_note;
    if (patch.unit_cost_ek !== undefined) body.unit_cost_ek = patch.unit_cost_ek;
    if (patch.surcharge_percent !== undefined) body.surcharge_percent = patch.surcharge_percent;
    if (patch.unit_price_vk !== undefined) body.unit_price_vk = patch.unit_price_vk;
    if (Object.keys(body).length === 0) return;

    setSavingId(p.id);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/${p.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'Speichern fehlgeschlagen');
      }
      const updated = (await res.json()) as LeadPosition;
      const merged = { ...updated, item_name: updated.item_name ?? '' };
      setPositions((prev) => prev.map((row) => (row.id === merged.id ? merged : row)).sort((a, b) => a.pos - b.pos));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      await loadPositions();
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (id: PositionRowId) => {
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Löschen fehlgeschlagen');
      await loadPositions();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = positions.findIndex((x) => String(x.id) === String(active.id));
    const newIndex = positions.findIndex((x) => String(x.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(positions, oldIndex, newIndex);
    setPositions(next.map((row, i) => ({ ...row, pos: i + 1 })));
    setReordering(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: next.map((row) => row.id) }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'Sortierung speichern fehlgeschlagen');
      }
      const data = await res.json();
      setPositions(normalizePositionsList(data));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      await loadPositions();
    } finally {
      setReordering(false);
    }
  };

  const filteredMaterials = React.useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return materials;
    return materials.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.description || '').toLowerCase().includes(q) ||
        (m.id || '').toLowerCase().includes(q)
    );
  }, [materials, pickerSearch]);

  const filteredLeistungen = React.useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return leistungen;
    return leistungen.filter(
      (l) =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.description || '').toLowerCase().includes(q) ||
        (l.id || '').toLowerCase().includes(q)
    );
  }, [leistungen, pickerSearch]);

  const sortableIds = React.useMemo(() => positions.map((p) => String(p.id)), [positions]);

  /** Hierarchical labels matching the offer editor (1 / 1.1 / 1.2 / '' for text+image).
   * The backend normalizes all custom_* source_types to 'custom', so we detect row kind
   * via the unit field: 'Titel' = title row, '' with custom source = text/image row. */
  const posLabels = React.useMemo(() => {
    const labels: string[] = [];
    let top = 0;
    let sub = 0;
    let underTitle = false;
    for (const p of positions) {
      const unit = (p.unit ?? '').trim();
      const st = p.source_type ?? '';
      const isTitle = unit === 'Titel';
      const isTextOrImage = unit === '' && st !== 'material' && st !== 'leistung';
      if (isTitle) {
        top += 1; sub = 0; underTitle = true;
        labels.push(String(top));
      } else if (isTextOrImage) {
        labels.push('');
      } else {
        if (underTitle) {
          sub += 1;
          labels.push(`${top}.${sub}`);
        } else {
          top += 1; sub = 0;
          labels.push(String(top));
        }
      }
    }
    return labels;
  }, [positions]);

  return (
    <section
      className={isFirst ? '' : 'mt-8 pt-8 border-t border-app-border'}
      aria-label={offeringMeta?.title ?? 'Positionen'}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:mb-4 px-4 sm:px-0">
        {offeringMeta ? (
          <p className="text-xs text-app-text-muted flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
            <span className="font-semibold text-app-text">{offeringMeta.title}</span>
            {offeringMeta.displayId ? (
              <span className="text-app-text-subtle tabular-nums">{offeringMeta.displayId}</span>
            ) : null}
            {offeringMeta.isPrimary ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-app-surface-muted text-app-text-muted border border-app-border/80">
                primär
              </span>
            ) : null}
            {offeringMeta.isLastUsed ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200/60">
                zuletzt verwendet
              </span>
            ) : null}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {canViewStammdaten ? (
            <>
              <button
                type="button"
                disabled={adding}
                onClick={() => setPicker('material')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover shadow-sm disabled:opacity-50"
              >
                <Package className="w-3.5 h-3.5 text-pipedrive-blue" />
                Material
              </button>
              <button
                type="button"
                disabled={adding}
                onClick={() => setPicker('leistung')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover shadow-sm disabled:opacity-50"
              >
                <Wrench className="w-3.5 h-3.5 text-pipedrive-blue" />
                Leistung
              </button>
            </>
          ) : null}
          <button
            type="button"
            disabled={adding}
            onClick={() => void addFreeRow()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover shadow-sm disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5 text-pipedrive-blue shrink-0" />
            Freie Position
          </button>
          {offeringId ? (
            <OfferImportActionButton
              offeringId={offeringId}
              onImported={loadPositions}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover shadow-sm"
            />
          ) : null}
          {offeringId ? (
            <GaebActionButton
              offeringId={offeringId}
              exportOnly
              label={
                <>
                  <FileText className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
                  GAEB-Export
                </>
              }
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover shadow-sm"
            />
          ) : null}
          {offeringId && idsEnabled ? (
            <IdsCartButton
              leadRouteId={leadRouteId}
              offeringId={offeringId}
              documentTypeLabel="Angebot"
              onImported={loadPositions}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover shadow-sm"
            />
          ) : null}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      <div className="bg-app-surface border-0 sm:border sm:border-app-border sm:rounded-lg sm:shadow-sm overflow-hidden sm:overflow-visible border-t border-app-border">
        {loading ? (
          <InlineLoadingState label="Lade Positionen…" className="py-16" />
        ) : positions.length === 0 ? (
          <div className="py-14 px-4 text-center text-app-text-subtle text-sm">
            Noch keine Positionen. Fügen Sie Material, Leistung oder eine freie Position hinzu.
          </div>
        ) : (
          <>
            {/* ── Mobile card list ── */}
            <div className="sm:hidden divide-y divide-app-border">
              {positions.map((p) => {
                const lineTotal = Number.isFinite(p.line_total) ? p.line_total : Math.round(p.quantity * p.unit_price_vk * 100) / 100;
                const isEditing = mobileEditId === p.id;
                return (
                  <div key={p.id} className="px-4 py-3">
                    {isEditing ? (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Name</label>
                          <input
                            className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm"
                            value={mobileEditDraft.item_name ?? p.item_name}
                            onChange={(e) => setMobileEditDraft((d) => ({ ...d, item_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Beschreibung</label>
                          <textarea
                            rows={2}
                            className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm resize-none"
                            value={mobileEditDraft.description !== undefined ? mobileEditDraft.description : (p.description || '')}
                            onChange={(e) => setMobileEditDraft((d) => ({ ...d, description: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block mb-0.5">Interne Notiz</label>
                          <textarea
                            rows={2}
                            className="w-full border border-amber-200 bg-amber-50/50 rounded px-2 py-1.5 text-sm resize-none"
                            value={mobileEditDraft.internal_note !== undefined ? mobileEditDraft.internal_note : (p.internal_note || '')}
                            onChange={(e) => setMobileEditDraft((d) => ({ ...d, internal_note: e.target.value }))}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Menge</label>
                            <GermanDecimalInput
                              className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm"
                              value={mobileEditDraft.quantity ?? p.quantity}
                              onValueChange={(quantity) => setMobileEditDraft((d) => ({ ...d, quantity }))}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Einheit</label>
                            <select
                              className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm bg-app-surface"
                              value={mobileEditDraft.unit ?? (p.unit || '')}
                              onChange={(e) => setMobileEditDraft((d) => ({ ...d, unit: e.target.value || null }))}
                            >
                              {offerUnitSelectOptions(mobileEditDraft.unit ?? p.unit).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">EK / Kosten (€)</label>
                            <GermanDecimalInput
                              className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm"
                              value={mobileEditDraft.unit_cost_ek !== undefined ? mobileEditDraft.unit_cost_ek : p.unit_cost_ek}
                              onValueChange={(ek) => {
                                const sur = mobileEditDraft.surcharge_percent !== undefined ? mobileEditDraft.surcharge_percent : p.surcharge_percent;
                                const vk = vkFromEkSurcharge(ek, sur);
                                setMobileEditDraft((d) => ({ ...d, unit_cost_ek: ek, unit_price_vk: vk }));
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Zuschlag %</label>
                            <GermanDecimalInput
                              className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm"
                              value={mobileEditDraft.surcharge_percent !== undefined ? mobileEditDraft.surcharge_percent : p.surcharge_percent}
                              onValueChange={(sur) => {
                                const ek = mobileEditDraft.unit_cost_ek !== undefined ? mobileEditDraft.unit_cost_ek : p.unit_cost_ek;
                                const vk = vkFromEkSurcharge(ek, sur);
                                setMobileEditDraft((d) => ({ ...d, surcharge_percent: sur, unit_price_vk: vk }));
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">VK / Einheit (€)</label>
                          <GermanDecimalInput
                            className="w-full border border-app-border-strong rounded px-2 py-1.5 text-sm"
                            value={mobileEditDraft.unit_price_vk !== undefined ? mobileEditDraft.unit_price_vk : p.unit_price_vk}
                            onValueChange={(unit_price_vk) => setMobileEditDraft((d) => ({ ...d, unit_price_vk }))}
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            type="button"
                            disabled={savingId === p.id}
                            onClick={async () => {
                              await saveRow(p, mobileEditDraft as LeadPositionPatch);
                              setMobileEditId(null);
                              setMobileEditDraft({});
                            }}
                            className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            {savingId === p.id ? 'Speichert…' : 'Speichern'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setMobileEditId(null); setMobileEditDraft({}); }}
                            className="flex-1 py-2 rounded-lg bg-app-surface-muted text-app-text-muted text-sm font-semibold"
                          >
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            onClick={async () => { await deleteRow(p.id); setMobileEditId(null); }}
                            className="py-2 px-3 rounded-lg bg-red-50 text-red-600 text-sm"
                            aria-label="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="w-full text-left"
                        onClick={() => { setMobileEditId(p.id); setMobileEditDraft({}); }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-app-text truncate">{p.item_name || '—'}</p>
                            <p className="text-xs text-app-text-subtle mt-0.5">
                              {p.quantity} {p.unit || ''}
                              {p.unit_price_vk ? ` · ${eurFmt.format(p.unit_price_vk)}/Einheit` : ''}
                            </p>
                          </div>
                          <span className="shrink-0 text-sm font-semibold text-app-text">{eurFmt.format(lineTotal)}</span>
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
              {/* Mobile totals */}
              <div className="px-4 py-3 bg-app-surface-muted/80 border-t border-app-border space-y-1">
                {discountAmount > 0 && (
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Zwischensumme</span><span className="tabular-nums">{eurFmt.format(rawNetSum)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-xs text-app-text-subtle">
                    <span>Rabatt{offerDiscount?.type === 'percent' ? ` (${offerDiscount.value.toLocaleString('de-DE')} %)` : ''}</span>
                    <span className="tabular-nums">−{eurFmt.format(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-app-text-subtle">
                  <span>Nettosumme</span><span className="font-semibold tabular-nums">{eurFmt.format(netSum)}</span>
                </div>
                {!hideVatAndGross && (
                  <div className="flex justify-between text-xs text-app-text-subtle">
                    <span>zzgl. {vatRatePercent}&nbsp;% MwSt</span><span className="tabular-nums">{eurFmt.format(vatAmount)}</span>
                  </div>
                )}
                {!hideVatAndGross && (
                  <div className="flex justify-between text-sm font-bold text-app-text pt-1 border-t border-app-border">
                    <span>Bruttosumme</span><span className="tabular-nums">{eurFmt.format(grossSum)}</span>
                  </div>
                )}
                {hideVatAndGross && vatLegalNote ? (
                  <p className="text-xs text-app-text-muted leading-snug pt-1">{vatLegalNote}</p>
                ) : null}
              </div>
            </div>

            {/* ── Desktop table ── */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
            <div className="hidden sm:block overflow-x-auto overflow-y-visible">
              <table className="w-full table-fixed text-sm min-w-[1180px]">
                <thead>
                  <tr className="bg-app-surface-muted/90 border-b border-app-border text-left">
                    <th className="px-1 py-2.5 w-8" aria-label="Sortieren" />
                    <th className="px-2 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-10">Pos.</th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-24">Menge</th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-28">Einheit</th>
                    {/* Name : Beschreibung = 1 : 2 der gemeinsamen Textspalten → w-1/6 : w-1/3 der Tabellenbreite */}
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-1/6 min-w-0">
                      Name
                    </th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-1/3 min-w-0">
                      Beschreibung
                    </th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-32 text-right whitespace-nowrap">
                      EK / Kosten
                    </th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-24 text-right">
                      Zuschlag %
                    </th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-32 text-right whitespace-nowrap">
                      Preis (VK) / Einheit
                    </th>
                    <th className="px-3 py-2.5 font-bold text-[10px] uppercase tracking-wider text-app-text-subtle w-28 text-right">Gesamt</th>
                    <th className="px-2 py-2.5 w-20" />
                  </tr>
                </thead>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  <tbody className="divide-y divide-app-border">
                    {positions.map((p, idx) => (
                      <SortablePositionRow
                        key={p.id}
                        id={String(p.id)}
                        position={p}
                        posLabel={posLabels[idx] ?? String(p.pos)}
                        onSave={saveRow}
                        onDelete={deleteRow}
                        saving={savingId === p.id}
                        reordering={reordering}
                      />
                    ))}
                  </tbody>
                </SortableContext>
                <tfoot>
                  {discountAmount > 0 && (
                    <tr className="bg-app-surface-muted/80 border-t border-app-border text-slate-400">
                      <td colSpan={9} className="px-3 py-2 text-right text-xs uppercase tracking-wide">
                        Zwischensumme
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs">{eurFmt.format(rawNetSum)}</td>
                      <td />
                    </tr>
                  )}
                  {discountAmount > 0 && (
                    <tr className="bg-app-surface-muted/80 text-app-text-muted">
                      <td colSpan={9} className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide">
                        Rabatt{offerDiscount?.type === 'percent' ? ` (${offerDiscount.value.toLocaleString('de-DE')} %)` : ''}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold">−{eurFmt.format(discountAmount)}</td>
                      <td />
                    </tr>
                  )}
                  <tr className="bg-app-surface-muted/80 border-t border-app-border text-app-text">
                    <td colSpan={9} className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-app-text-subtle">
                      Nettosumme
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{eurFmt.format(netSum)}</td>
                    <td />
                  </tr>
                  {!hideVatAndGross && (
                    <tr className="bg-app-surface-muted/80 text-app-text">
                      <td colSpan={9} className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-app-text-subtle">
                        zzgl. {vatRatePercent}&nbsp;% MwSt
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-medium">{eurFmt.format(vatAmount)}</td>
                      <td />
                    </tr>
                  )}
                  {!hideVatAndGross && (
                    <tr className="bg-app-surface-muted/90 border-t border-app-border text-app-text">
                      <td colSpan={9} className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wide text-app-text-muted">
                        Bruttosumme
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold text-base">{eurFmt.format(grossSum)}</td>
                      <td />
                    </tr>
                  )}
                  {hideVatAndGross && vatLegalNote ? (
                    <tr className="bg-app-surface-muted/80 border-t border-app-border text-app-text-muted">
                      <td colSpan={10} className="px-3 py-3 text-xs leading-snug text-left">
                        {vatLegalNote}
                      </td>
                      <td />
                    </tr>
                  ) : null}
                </tfoot>
              </table>
            </div>
          </DndContext>
          </>
        )}
      </div>

      <AppModal
        open={picker != null}
        onClose={() => setPicker(null)}
        overlayClassName="z-[225]"
        panelClassName="md:max-w-lg md:!max-h-[min(80vh,560px)]"
        ariaLabelledBy="produkte-picker-title"
      >
        <AppModalHeader
          titleId="produkte-picker-title"
          title={picker === 'material' ? 'Material aus Stammdaten' : 'Leistung aus Stammdaten'}
          onClose={() => setPicker(null)}
        />
        <div className="shrink-0 border-b border-app-border px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Suchen…"
              className="w-full pl-9 pr-3 py-2 border border-app-border rounded-lg text-sm focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 px-3 py-2">
          {picker === 'material' ? (
            filteredMaterials.length === 0 ? (
              <p className="text-sm text-app-text-subtle px-2 py-6 text-center">Keine Materialien gefunden.</p>
            ) : (
              <ul className="space-y-1">
                {filteredMaterials.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      disabled={adding}
                      onClick={() => void addFromStamm('material', m.id)}
                      className="w-full text-left px-3 py-2.5 rounded-lg border border-app-border hover:bg-app-hover text-sm font-medium text-app-text disabled:opacity-50"
                    >
                      <span className="block truncate">{m.name}</span>
                      <span className="text-xs text-app-text-subtle font-normal">
                        {m.unit ? `${m.unit} · ` : ''}
                        EK {m.net_price != null ? eurFmt.format(m.net_price) : '—'}
                        {m.surcharge_percent != null ? ` · ${pctFmt.format(m.surcharge_percent)} %` : ''}
                        {m.gross_price != null ? ` → VK ${eurFmt.format(m.gross_price)}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : filteredLeistungen.length === 0 ? (
            <p className="text-sm text-app-text-subtle px-2 py-6 text-center">Keine Leistungen gefunden.</p>
          ) : (
            <ul className="space-y-1">
              {filteredLeistungen.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    disabled={adding}
                    onClick={() => void addFromStamm('leistung', l.id)}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-app-border hover:bg-app-hover text-sm font-medium text-app-text disabled:opacity-50"
                  >
                    <span className="block truncate">{l.name}</span>
                    <span className="text-xs text-app-text-subtle font-normal">
                      {l.unit ? `${l.unit} · ` : ''}
                      EK {l.net_cost != null ? eurFmt.format(l.net_cost) : '—'}
                      {l.surcharge_percent != null ? ` · ${pctFmt.format(l.surcharge_percent)} %` : ''}
                      {l.gross_price != null ? ` → VK ${eurFmt.format(l.gross_price)}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </AppModal>
    </section>
  );
};

export const LeadProdukteTab: React.FC<LeadProdukteTabProps> = ({
  leadRouteId,
  offeringSections = [],
  onNetSumChange,
  onCreateDocument,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [page, setPage] = React.useState(0);
  const sectionNetSumsRef = React.useRef<Record<string, number>>({});
  const offeringIdsKey = offeringSections.map((s) => s.offeringId).join(',');

  const totalOfferings = offeringSections.length;
  const pageCount = Math.max(1, Math.ceil(totalOfferings / OFFERINGS_PAGE_SIZE));
  const paginateOfferings = totalOfferings > OFFERINGS_PAGE_SIZE;

  React.useEffect(() => {
    setPage((p) => {
      if (totalOfferings === 0) return 0;
      return Math.min(p, pageCount - 1);
    });
  }, [totalOfferings, pageCount]);

  React.useEffect(() => {
    setPage(0);
    sectionNetSumsRef.current = {};
  }, [leadRouteId, offeringIdsKey]);

  const pageStart = page * OFFERINGS_PAGE_SIZE;
  const visibleSections = paginateOfferings
    ? offeringSections.slice(pageStart, pageStart + OFFERINGS_PAGE_SIZE)
    : offeringSections;
  const rangeFrom = totalOfferings === 0 ? 0 : pageStart + 1;
  const rangeTo = Math.min(pageStart + OFFERINGS_PAGE_SIZE, totalOfferings);

  const handleSectionNetSum = React.useCallback(
    (offeringId: string, netSum: number) => {
      if (paginateOfferings) return;
      sectionNetSumsRef.current[offeringId] = netSum;
      const total = Object.values(sectionNetSumsRef.current).reduce((a, b) => a + b, 0);
      onNetSumChange?.(total);
    },
    [onNetSumChange, paginateOfferings],
  );

  React.useEffect(() => {
    sectionNetSumsRef.current = {};
    if (!offeringSections.length) {
      onNetSumChange?.(0);
    }
  }, [offeringSections, onNetSumChange]);

  const goToPage = React.useCallback((next: number) => {
    setPage(next);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sections = offeringSections.length > 0 ? visibleSections : null;

  const paginationBar =
    paginateOfferings && totalOfferings > 0 ? (
      <div className="flex flex-wrap items-center justify-between gap-2 border border-app-border rounded-lg bg-app-surface-muted/80 px-4 py-2.5 text-xs text-app-text-muted mx-4 sm:mx-0">
        <span className="tabular-nums">
          {rangeFrom}–{rangeTo} von {totalOfferings} Angeboten · Seite {page + 1} / {pageCount}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 0}
            onClick={() => goToPage(Math.max(0, page - 1))}
            className="inline-flex items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 py-1.5 font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40 disabled:pointer-events-none"
          >
            <ChevronLeft className="w-3.5 h-3.5" aria-hidden />
            Zurück
          </button>
          <button
            type="button"
            disabled={page >= pageCount - 1}
            onClick={() => goToPage(Math.min(pageCount - 1, page + 1))}
            className="inline-flex items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 py-1.5 font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40 disabled:pointer-events-none"
          >
            Weiter
            <ChevronRight className="w-3.5 h-3.5" aria-hidden />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div
      ref={scrollRef}
      className="flex-1 min-h-0 overflow-y-auto px-0 py-0 sm:p-6 bg-app-surface sm:bg-transparent"
    >
      <div className="hidden sm:flex sm:flex-row sm:items-center sm:justify-between gap-3 sm:mb-6 px-0">
        <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
          <Package className="w-4 h-4 shrink-0" />
          Materialien &amp; Leistungen
        </h2>
        {onCreateDocument ? (
          <button
            type="button"
            onClick={() => onCreateDocument()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-xs sm:text-sm font-semibold hover:bg-blue-600 shadow-sm"
          >
            <FilePlus className="w-3.5 h-3.5 shrink-0" />
            Dokument erstellen
          </button>
        ) : null}
      </div>

      {paginationBar ? <div className="mb-4 sm:mb-6">{paginationBar}</div> : null}

      {sections ? (
        sections.map((section, index) => (
          <LeadProdukteOfferingSection
            key={section.offeringId}
            leadRouteId={leadRouteId}
            offeringId={section.offeringId}
            offeringMeta={section.offeringMeta}
            offerDiscount={section.offerDiscount}
            offerVatMode={section.offerVatMode}
            isFirst={index === 0}
            onNetSumChange={(netSum) => handleSectionNetSum(section.offeringId, netSum)}
          />
        ))
      ) : (
        <LeadProdukteOfferingSection
          leadRouteId={leadRouteId}
          offeringId={null}
          isFirst
          onNetSumChange={(netSum) => onNetSumChange?.(netSum)}
        />
      )}

      {paginationBar ? <div className="mt-6">{paginationBar}</div> : null}
    </div>
  );
};

function SortablePositionRow({
  id,
  position: p,
  posLabel,
  onSave,
  onDelete,
  saving,
  reordering,
}: {
  id: string;
  position: LeadPosition;
  posLabel: string;
  onSave: (p: LeadPosition, patch: LeadPositionPatch) => void;
  onDelete: (id: PositionRowId) => void;
  saving: boolean;
  reordering: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 2 : undefined,
    position: 'relative' as const,
  };

  const [qtyStr, setQtyStr] = React.useState(String(p.quantity));
  const [nameStr, setNameStr] = React.useState(p.item_name ?? '');
  const [descStr, setDescStr] = React.useState(p.description);
  const [noteStr, setNoteStr] = React.useState(p.internal_note ?? '');
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [notePos, setNotePos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const noteButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const notePanelRef = React.useRef<HTMLDivElement | null>(null);
  const [ekStr, setEkStr] = React.useState(toInputDecimal(p.unit_cost_ek));
  const [surStr, setSurStr] = React.useState(toInputPercent(p.surcharge_percent));
  const [priceStr, setPriceStr] = React.useState(toInputDecimal(p.unit_price_vk));

  React.useEffect(() => {
    setQtyStr(String(p.quantity));
    setNameStr(p.item_name ?? '');
    setDescStr(p.description);
    setNoteStr(p.internal_note ?? '');
    setNoteOpen(false);
    setEkStr(toInputDecimal(p.unit_cost_ek));
    setSurStr(toInputPercent(p.surcharge_percent));
    setPriceStr(toInputDecimal(p.unit_price_vk));
  }, [p.id, p.quantity, p.unit, p.item_name, p.description, p.internal_note, p.unit_cost_ek, p.surcharge_percent, p.unit_price_vk]);

  React.useEffect(() => {
    if (!noteOpen) return;
    const updatePosition = () => {
      const btn = noteButtonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const panelWidth = 320;
      const left = Math.max(8, Math.min(window.innerWidth - panelWidth - 8, r.right - panelWidth));
      const top = Math.min(window.innerHeight - 16, r.bottom + 6);
      setNotePos({ top, left });
    };
    updatePosition();
    const onPointerDown = (ev: PointerEvent) => {
      const t = ev.target as Node;
      if (notePanelRef.current?.contains(t)) return;
      if (noteButtonRef.current?.contains(t)) return;
      setNoteOpen(false);
      setNoteStr(p.internal_note ?? '');
    };
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [noteOpen, p.internal_note]);

  const linePreview = React.useMemo(() => {
    const q = parseGermanNumber(qtyStr) || 0;
    const pr = parseGermanNumber(priceStr);
    return Math.round(q * pr * 100) / 100;
  }, [qtyStr, priceStr]);

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-app-hover/80 bg-app-surface">
      <td className="px-1 py-2 align-middle w-8">
        <button
          type="button"
          className="p-1 rounded text-slate-400 hover:text-app-text-muted hover:bg-slate-200 cursor-grab active:cursor-grabbing touch-none disabled:opacity-40"
          disabled={reordering}
          aria-label="Position verschieben"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      <td className="px-2 py-2 align-middle text-app-text-muted font-medium tabular-nums text-center">{posLabel}</td>
      <td className="px-3 py-2 align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={qtyStr}
          onChange={(e) => setQtyStr(e.target.value)}
          onBlur={() => {
            const quantity = parseGermanNumber(qtyStr) || 0;
            if (quantity !== p.quantity) void onSave(p, { quantity });
          }}
          className="w-full min-w-[3.5rem] px-2 py-1.5 border border-app-border rounded text-app-text tabular-nums text-sm"
        />
      </td>
      <td className="px-3 py-2 align-middle min-w-[7.5rem]">
        <AppSelect
          id={`lead-pos-unit-${p.id}`}
          size="sm"
          className="[&_button]:min-h-0 [&_button]:py-1.5 [&_button]:px-2 [&_button]:text-xs [&_button]:rounded-md"
          value={p.unit ?? ''}
          options={offerUnitSelectOptions(p.unit)}
          disabled={saving || reordering}
          onChange={(v) => {
            const u = v.trim() || null;
            if (u !== (p.unit ?? null)) void onSave(p, { unit: u });
          }}
        />
      </td>
      <td className="px-3 py-2 align-middle w-1/6 min-w-0">
        <input
          type="text"
          value={nameStr}
          onChange={(e) => setNameStr(e.target.value)}
          onBlur={() => {
            const next = nameStr.trim();
            if (next !== (p.item_name ?? '').trim()) void onSave(p, { item_name: next });
          }}
          placeholder="z. B. Wärmepumpe"
          disabled={saving || reordering}
          className="w-full min-w-0 max-w-full px-2 py-1.5 border border-app-border rounded text-app-text text-sm disabled:opacity-60"
        />
      </td>
      <td className="px-3 py-2 align-middle w-1/3 min-w-0">
        <input
          type="text"
          value={descStr}
          onChange={(e) => setDescStr(e.target.value)}
          onBlur={() => {
            if (descStr !== p.description) void onSave(p, { description: descStr });
          }}
          className="w-full min-w-0 max-w-full px-2 py-1.5 border border-app-border rounded text-app-text text-sm"
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={ekStr}
          onChange={(e) => setEkStr(e.target.value)}
          onBlur={() => {
            const ek = parseGermanNumber(ekStr);
            setEkStr(toInputDecimal(ek));
            const sur = parseGermanNumber(surStr);
            const vk = vkFromEkSurcharge(ek, sur);
            setPriceStr(toInputDecimal(vk));
            if (Math.abs(ek - p.unit_cost_ek) > 0.0001) void onSave(p, { unit_cost_ek: ek });
          }}
          className="w-full min-w-[5.5rem] px-2 py-1.5 border border-app-border rounded text-app-text text-right tabular-nums text-sm"
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={surStr}
          onChange={(e) => setSurStr(e.target.value)}
          onBlur={() => {
            const sur = parseGermanNumber(surStr);
            setSurStr(toInputPercent(sur));
            const ek = parseGermanNumber(ekStr);
            const vk = vkFromEkSurcharge(ek, sur);
            setPriceStr(toInputDecimal(vk));
            if (Math.abs(sur - p.surcharge_percent) > 0.0001) void onSave(p, { surcharge_percent: sur });
          }}
          className="w-full min-w-[4rem] px-2 py-1.5 border border-app-border rounded text-app-text text-right tabular-nums text-sm"
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <input
          type="text"
          inputMode="decimal"
          value={priceStr}
          onChange={(e) => setPriceStr(e.target.value)}
          onBlur={() => {
            const vk = parseGermanNumber(priceStr);
            setPriceStr(toInputDecimal(vk));
            if (Math.abs(vk - p.unit_price_vk) > 0.0001) void onSave(p, { unit_price_vk: vk });
          }}
          className="w-full min-w-[6rem] px-2 py-1.5 border border-app-border rounded text-app-text text-right tabular-nums text-sm"
        />
      </td>
      <td className="px-3 py-2 align-middle text-right tabular-nums font-medium text-app-text">
        {saving ? <LoadingSpinner className="inline text-slate-400" /> : eurFmt.format(linePreview)}
      </td>
      <td className="px-2 py-2 align-middle relative">
        <div className="flex items-center justify-center gap-1">
          <button
            ref={noteButtonRef}
            type="button"
            onClick={() => setNoteOpen((v) => !v)}
            className={`p-1.5 rounded-lg transition-colors ${noteStr.trim() ? 'text-amber-700 hover:text-amber-800 hover:bg-amber-50' : 'text-slate-400 hover:text-app-text-muted hover:bg-app-hover'}`}
            title="Interne Notiz"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(p.id)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Entfernen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        {noteOpen ? createPortal(
          <div
            ref={notePanelRef}
            className="fixed z-[300] w-80 max-w-[calc(100vw-16px)] rounded-lg border border-amber-200 bg-app-surface shadow-lg p-2"
            style={{ top: notePos.top, left: notePos.left }}
          >
            <label className="block text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">
              Interne Notiz
            </label>
            <textarea
              rows={4}
              value={noteStr}
              onChange={(e) => setNoteStr(e.target.value)}
              placeholder="Nur intern sichtbar, nicht im Kundendokument"
              className="w-full border border-amber-200 bg-amber-50/40 rounded px-2 py-1.5 text-xs text-amber-900 resize-y"
            />
            <div className="mt-2 flex items-center justify-end gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setNoteStr(p.internal_note ?? '');
                  setNoteOpen(false);
                }}
                className="px-2 py-1 rounded text-xs text-app-text-muted hover:bg-app-hover"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => {
                  if (noteStr !== (p.internal_note ?? '')) void onSave(p, { internal_note: noteStr });
                  setNoteOpen(false);
                }}
                className="px-2 py-1 rounded text-xs bg-amber-600 text-white hover:bg-amber-700"
              >
                Speichern
              </button>
            </div>
          </div>,
          document.body
        ) : null}
      </td>
    </tr>
  );
}
